import { applyAction, type MatchState, type Pcg32, type Side } from '../engine';
import { BALANCED, GREEDY_SETTINGS } from './personality';
import { searchPlans, type Plan } from './planner';
import type { EvalContext } from './valuation';

/** Re-deals one side's hidden cards (hand and deck) at random, keeping their counts. */
export function redealHidden(s: MatchState, side: Side, rng: Pcg32): MatchState {
  const p = s.players[side];
  const pool = rng.shuffle([...p.hand, ...p.deck]);
  return {
    ...s,
    players: {
      ...s.players,
      [side]: { ...p, hand: pool.slice(0, p.hand.length), deck: pool.slice(p.hand.length) },
    },
  };
}

/**
 * The opponent's best immediate gain after a plan, averaged over re-dealt hidden hands:
 * a one-ply lookahead that catches exposures the static threat estimate misses.
 */
export function replyGain(
  end: MatchState,
  ctx: EvalContext,
  rng: Pcg32,
  samples: number,
): { gain: number; nodes: number } {
  const opp = ctx.opp;
  if (end.phase !== 'TURN_START' || end.decider !== opp) return { gain: 0, nodes: 0 };
  const oppCtx: EvalContext = {
    ...ctx,
    me: opp,
    opp: ctx.me,
    personality: BALANCED,
    settings: GREEDY_SETTINGS,
    cardValue: 1,
  };
  let total = 0;
  let nodes = 0;
  for (let i = 0; i < samples; i++) {
    const dealt = redealHidden(end, opp, rng);
    const r = applyAction(dealt, { type: 'CONTINUE', side: opp });
    if (r.error || r.state.phase !== 'ACTION' || r.state.decider !== opp) continue;
    const res = searchPlans(r.state, oppCtx);
    nodes += res.nodes;
    total += res.plans.reduce((m, p) => Math.max(m, p.gain), 0);
  }
  return { gain: total / samples, nodes };
}

/** Rescores the best plans with the opponent's simulated reply (settings.refine plans). */
export function refinePlans(plans: Plan[], ctx: EvalContext, rng: Pcg32): number {
  const n = Math.min(ctx.settings.refine, plans.length);
  if (n <= 1) return 0;
  let nodes = 0;
  const samples = Math.max(1, ctx.settings.samples);
  for (let i = 0; i < n; i++) {
    const plan = plans[i] as Plan;
    const r = replyGain(plan.end, ctx, rng, samples);
    nodes += r.nodes;
    plan.value -= ctx.personality.caution * 0.55 * r.gain;
  }
  // Only the refined plans are comparable with each other now; keep them ahead of the rest.
  const refined = plans.slice(0, n).sort((a, b) => b.value - a.value);
  plans.splice(0, n, ...refined);
  return nodes;
}
